var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { none, some } from 'fp-ts/lib/Option';
import { asks } from 'fp-ts/lib/Reader';
import { Subject } from 'rxjs';
import { map, scan, shareReplay } from 'rxjs/operators';
import { toRemoteData } from '../../utils/axios';
import { mapRD, tapRD } from '../../utils/object.utils';
import { IntegrationType } from './integration.utils';
function parseApiIntegrationType(type) {
    switch (type) {
        case 'trello':
            return some(IntegrationType.Trello);
        case 'jira':
            return some(IntegrationType.Jira);
        case 'chathq':
            return some(IntegrationType.ChatHQ);
        case 'slack':
            return some(IntegrationType.Slack);
    }
    return none;
}
function serializeApiIntegrationType(type) {
    switch (type) {
        case IntegrationType.Trello:
            return 'trello';
        case IntegrationType.Asana:
            return 'asana';
        case IntegrationType.Jira:
            return 'jira';
        case IntegrationType.ChatHQ:
            return 'chathq';
        case IntegrationType.Slack:
            return 'slack';
    }
}
export var integrationStore = asks(function (_a) {
    var axios = _a.axios;
    var doGetAll = function (projectId) {
        return toRemoteData(axios.get("/api/products/" + projectId + "/integrations")).pipe(mapRD(function (intg) { return intg.map(function (i) { return (__assign(__assign({}, i), { projectId: projectId })); }); }));
    };
    var updates$ = new Subject();
    var set = function (projectId, type, secrets, config, notifications) {
        return toRemoteData(axios.post("/api/products/" + projectId + "/integrations", {
            vendor: serializeApiIntegrationType(type),
            secret_details: secrets,
            config_details: config,
            notification_details: notifications,
        })).pipe(mapRD(function (intg) { return (__assign(__assign({}, intg), { projectId: projectId })); }), tapRD(function (result) { return updates$.next([result]); }));
    };
    var update = function (projectId, integrationId, secrets, config, notifications) {
        return toRemoteData(axios.patch("/api/products/" + projectId + "/integrations/" + integrationId, {
            secret_details: secrets,
            config_details: config,
            notification_details: notifications,
        })).pipe(mapRD(function (intg) { return (__assign(__assign({}, intg), { projectId: projectId })); }), tapRD(function (result) { return updates$.next([result]); }));
    };
    var getAll = function (projectId) {
        return doGetAll(projectId).pipe(tapRD(function (result) { return updates$.next(result); }), mapRD(convertToMap));
    };
    var remove = function (projectId, integrationId) {
        return toRemoteData(axios.delete("/api/products/" + projectId + "/integrations/" + integrationId)).pipe(tapRD(function () {
            return updates$.next([
                {
                    id: integrationId,
                },
            ]);
        }));
    };
    var all$ = updates$.pipe(scan(function (state, updates) {
        return updates.reduce(function (state, update) {
            var existingId = state.some(function (s) { return s.id === update.id; });
            if ('vendor' in update) {
                return existingId ? state.map(function (s) { return (s.id === update.id ? update : s); }) : __spreadArray(__spreadArray([], state, true), [update], false);
            }
            else {
                return state.filter(function (s) { return s.id !== update.id; });
            }
        }, state);
    }, []), shareReplay(1));
    var getAllWithUpdates = function (projectId) {
        getAll(projectId).subscribe();
        return all$.pipe(map(function (all) { return all.filter(function (intg) { return intg.projectId === projectId; }); }), map(convertToMap), shareReplay(1));
    };
    return {
        set: set,
        getAll: getAll,
        remove: remove,
        getAllWithUpdates: getAllWithUpdates,
        update: update,
    };
});
function convertToMap(integrations) {
    return integrations.reduce(function (acc, item) {
        return parseApiIntegrationType(item.vendor).fold(acc, function (type) {
            var _a;
            return (__assign(__assign({}, acc), (_a = {}, _a[type] = {
                config_details: item.config_details,
                notification_details: item.notification_details,
                id: item.id,
            }, _a)));
        });
    }, {});
}
//# sourceMappingURL=integration.store.js.map